<template>
  <div class="bills-layout">
    <main-navigation />

    <div class="bills-page">
      <div class="bills-container">
        
        <!-- Compact Header with Generate Button -->
        <div class="bills-header">
          <div class="header-title">
            <h2>
              <span class="title-icon"><i class="bi bi-cash-stack"></i></span>
              Bills
            </h2>
            <span class="bills-count-badge">{{ bills.length }} total</span>
          </div>
          
          <button 
            @click="createBills" 
            :disabled="loading"
            class="btn-generate"
            :class="{ 'btn-loading': loading }"
          >
            <span class="btn-icon" v-if="!loading">+</span>
            <span class="btn-spinner" v-if="loading"></span>
            <span class="btn-text">{{ loading ? "Generating..." : "Generate" }}</span>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="search-section">
          <div class="search-wrapper">
            <span class="search-icon"><i class="bi bi-search"></i></span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by tenant, bill name, or amount..."
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search"><i class="bi bi-x"></i></button>
          </div>
        </div>

        <!-- Messages -->
        <transition name="fade">
          <div v-if="successMessage" class="message-box success">
            <i class="bi bi-check-circle-fill"></i> {{ successMessage }}
            <button @click="successMessage = ''" class="message-close"><i class="bi bi-x"></i></button>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="errorMessage" class="message-box error">
            <i class="bi bi-exclamation-circle-fill"></i> {{ errorMessage }}
            <button @click="errorMessage = ''" class="message-close"><i class="bi bi-x"></i></button>
          </div>
        </transition>

        <!-- Bills List -->
        <div class="bills-list-container">
          <div v-if="billsLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading bills...</p>
          </div>

          <div v-else-if="filteredBills.length" class="bills-list">
            <div 
              v-for="bill in paginatedBills" 
              :key="bill.id" 
              class="bill-item"
            >
              <div class="bill-item-main">
                <!-- Tenant Info -->
                <div class="tenant-info">
                  <div class="tenant-avatar">{{ getInitials(bill.Tenant?.tenant_name) }}</div>
                  <div class="tenant-details">
                    <router-link 
                      :to="`/bill/${bill.id}`" 
                      class="tenant-link"
                    >
                      {{ bill.Tenant?.tenant_name || 'Unknown Tenant' }}
                    </router-link>
                    <span class="tenant-phone">{{ bill.Tenant?.tenant_phone || 'No phone' }}</span>
                  </div>
                </div>

                <!-- Unit Info -->
                <div class="unit-info">
                  <span class="unit-label">Unit</span>
                  <span class="unit-number">Unit #{{ bill.unit_id || '—' }}</span>
                </div>

                <!-- Bill Details -->
                <div class="bill-details">
                  <span class="bill-type" :class="getBillTypeClass(bill.bill_name)">
                    {{ bill.bill_name }}
                  </span>
                  <span class="bill-amount">KES {{ formatAmount(bill.amount) }}</span>
                </div>

                <!-- Period -->
                <div class="period-info">
                  <span class="period-label">Period</span>
                  <span class="period-date">{{ formatPeriod(bill) }}</span>
                </div>

                <!-- Status & Actions -->
                <div class="bill-actions">
                  <span class="status-badge" :class="getStatusClass(bill)">
                    {{ bill.processed ? 'Processed' : 'Pending' }}
                  </span>
                  <div class="action-buttons">
                    <button class="action-btn view-btn" @click="viewBillDetails(bill)" title="View Details">
                      <i class="bi bi-eye"></i>
                    </button>
                    <button class="action-btn download-btn" @click="downloadBill(bill)" title="Download">
                      ⬇️
                    </button>
                  </div>
                </div>
              </div>

              <!-- Description (if exists) -->
              <div v-if="bill.description" class="bill-description">
                {{ bill.description }}
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination">
              <button 
                @click="currentPage--" 
                :disabled="currentPage === 1"
                class="page-btn"
              >
                ←
              </button>
              <span class="page-info">
                {{ currentPage }} / {{ totalPages }}
              </span>
              <button 
                @click="currentPage++" 
                :disabled="currentPage === totalPages"
                class="page-btn"
              >
                →
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon"><i class="bi bi-cash-stack"></i></div>
            <p v-if="searchQuery">No bills matching "{{ searchQuery }}"</p>
            <p v-else>No bills yet</p>
            <button v-if="!bills.length" class="generate-btn-small" @click="fetchBills">
              Refresh
            </button>
          </div>
        </div>

        <!-- Debug Panel (optional) -->
        <div v-if="debugMode" class="debug-panel">
          <h4>Debug Info</h4>
          <pre>{{ JSON.stringify(bills.slice(0, 2), null, 2) }}</pre>
          <button @click="fetchBills" class="debug-btn">Refresh Data</button>
          <button @click="debugMode = false" class="debug-btn close">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  data() {
    return {
      loading: false,
      billsLoading: false,
      successMessage: "",
      errorMessage: "",
      bills: [],
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10,
      debugMode: false
    };
  },

  computed: {
    filteredBills() {
      if (!this.searchQuery) return this.bills;
      
      const query = this.searchQuery.toLowerCase();
      return this.bills.filter(bill => {
        const tenantName = bill.Tenant?.tenant_name?.toLowerCase() || '';
        const billName = bill.bill_name?.toLowerCase() || '';
        const description = bill.description?.toLowerCase() || '';
        const amount = bill.amount?.toString() || '';
        
        return (
          tenantName.includes(query) ||
          billName.includes(query) ||
          description.includes(query) ||
          amount.includes(query)
        );
      });
    },

    paginatedBills() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBills.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredBills.length / this.itemsPerPage);
    }
  },

  mounted() {
    this.fetchBills();
  },

  methods: {
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return Number(amount).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatPeriod(bill) {
      if (bill.billing_month && bill.billing_year) {
        const month = bill.billing_month.toString().padStart(2, '0');
        return `${month}/${bill.billing_year}`;
      }
      return 'N/A';
    },

    getBillTypeClass(billName) {
      if (!billName) return 'other';
      
      const name = billName.toLowerCase();
      if (name.includes('rent')) return 'rent';
      if (name.includes('service')) return 'service';
      if (name.includes('other')) return 'other';
      return 'other';
    },

    getStatusClass(bill) {
      return bill.processed ? 'status-processed' : 'status-pending';
    },

    async fetchBills() {
      this.billsLoading = true;
      this.errorMessage = "";

      try {
        const response = await apiFetch("/get/bills");
        
        // Handle different response structures
        if (Array.isArray(response)) {
          this.bills = response;
        } else if (response?.bills) {
          this.bills = response.bills;
        } else if (response?.data) {
          this.bills = response.data;
        } else {
          this.bills = [];
        }
        
        console.log("Bills loaded:", this.bills);
        this.currentPage = 1;
      } catch (error) {
        console.error("Error fetching bills:", error);
        this.errorMessage = "Failed to load bills";
        this.bills = [];
      } finally {
        this.billsLoading = false;
      }
    },

    async createBills() {
      if (!confirm("Generate bills for this month?")) return;

      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        const response = await apiFetch("/create/bills", {
          method: "POST"
        });
        
        const count = response.total_created || response.count || 0;
        this.successMessage = `Generated ${count} bills`;
        
        await this.fetchBills();

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);

      } catch (error) {
        console.error("Error creating bills:", error);
        this.errorMessage = "Failed to generate bills";
      } finally {
        this.loading = false;
      }
    },

    viewBillDetails(bill) {
      this.$router.push(`/bill/${bill.id}`);
    },

    downloadBill(bill) {
      // Implement download functionality
      console.log("Download bill:", bill);
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped>
.bills-layout {
  min-height: 100vh;
  background: var(--dash-bg);
}

.bills-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.bills-container {
  background: white;
  border-radius: var(--dash-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* Compact Header */
.bills-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--dash-line);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  font-size: 1.25rem;
}

.bills-count-badge {
  background: var(--dash-bg-alt);
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 0.8rem;
  color: var(--dash-muted);
  font-weight: 500;
}

/* Generate Button */
.btn-generate {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: var(--dash-pill);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  min-width: 100px;
  justify-content: center;
}

.btn-generate:hover:not(:disabled) {
  background: var(--dash-primary-hover);
  transform: translateY(-1px);
  box-shadow: var(--dash-shadow);
}

.btn-generate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 1.1rem;
  font-weight: 600;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Search Section */
.search-section {
  padding: 12px 20px;
  border-bottom: 1px solid var(--dash-line);
  background: var(--dash-surface-2);
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: var(--dash-faint);
  font-size: 0.9rem;
}

.search-input {
  width: 100%;
  padding: 10px 35px 10px 35px;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius);
  font-size: 0.9rem;
  transition: all 0.2s;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: var(--dash-faint);
  cursor: pointer;
  padding: 4px 8px;
  font-size: 0.9rem;
  border-radius: var(--dash-pill);
}

.clear-search:hover {
  background: var(--dash-bg-alt);
  color: var(--dash-muted);
}

/* Messages */
.message-box {
  margin: 12px 20px;
  padding: 10px 16px;
  border-radius: var(--dash-radius-xs);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid;
}

.message-box.success {
  background: var(--dash-success-soft);
  border-color: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.message-box.error {
  background: var(--dash-danger-soft);
  border-color: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.message-close {
  margin-left: auto;
  background: none;
  border: none;
  color: currentColor;
  opacity: 0.6;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: var(--dash-pill);
}

.message-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

/* Bills List */
.bills-list-container {
  padding: 20px;
}

.bills-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bill-item {
  background: white;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius-sm);
  transition: all 0.2s;
  overflow: hidden;
}

.bill-item:hover {
  border-color: var(--dash-line-strong);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.bill-item-main {
  padding: 16px;
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr auto;
  gap: 16px;
  align-items: center;
}

/* Tenant Info */
.tenant-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.tenant-avatar {
  width: 40px;
  height: 40px;
  background: var(--dash-primary);
  border-radius: var(--dash-radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.tenant-details {
  min-width: 0;
  flex: 1;
}

.tenant-link {
  color: var(--dash-ink);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tenant-link:hover {
  color: var(--dash-primary);
  text-decoration: underline;
}

.tenant-phone {
  font-size: 0.8rem;
  color: var(--dash-muted);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Unit Info */
.unit-info {
  display: flex;
  flex-direction: column;
}

.unit-label {
  font-size: 0.7rem;
  color: var(--dash-faint);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.unit-number {
  font-weight: 500;
  color: var(--dash-text);
  font-size: 0.95rem;
}

/* Bill Details */
.bill-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bill-type {
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-block;
  width: fit-content;
}

.bill-type.rent {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.bill-type.service {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.bill-type.other {
  background: var(--dash-alt-soft);
  color: var(--dash-alt-ink);
}

.bill-amount {
  font-weight: 600;
  color: var(--dash-success);
  font-size: 1rem;
}

/* Period Info */
.period-info {
  display: flex;
  flex-direction: column;
}

.period-label {
  font-size: 0.7rem;
  color: var(--dash-faint);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.period-date {
  font-size: 0.9rem;
  color: var(--dash-text);
}

/* Bill Actions */
.bill-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-pending {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.status-processed {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.action-buttons {
  display: flex;
  gap: 6px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--dash-pill);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-btn {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.view-btn:hover {
  background: var(--dash-primary-line);
}

.download-btn {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-hover);
}

.download-btn:hover {
  background: var(--dash-primary-soft);
}

/* Bill Description */
.bill-description {
  padding: 12px 16px;
  background: var(--dash-surface-2);
  border-top: 1px solid var(--dash-line);
  font-size: 0.85rem;
  color: var(--dash-muted);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px 0;
}

.page-btn {
  width: 36px;
  height: 36px;
  border: 1px solid var(--dash-line);
  background: white;
  border-radius: var(--dash-radius-xs);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled) {
  background: var(--dash-bg);
  border-color: var(--dash-line-strong);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  color: var(--dash-muted);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-sm);
  border: 1px dashed var(--dash-line-strong);
}

.empty-icon {
  font-size: 2.25rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  color: var(--dash-muted);
  margin: 0 0 16px 0;
  font-size: 0.95rem;
}

.generate-btn-small {
  padding: 8px 20px;
  border: none;
  border-radius: var(--dash-pill);
  background: var(--dash-primary);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.generate-btn-small:hover {
  background: var(--dash-primary-hover);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--dash-bg-alt);
  border-top-color: var(--dash-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-state p {
  color: var(--dash-muted);
  margin: 0;
}

/* Debug Panel */
.debug-panel {
  margin: 20px;
  padding: 16px;
  background: var(--dash-ink);
  border-radius: var(--dash-radius-xs);
  color: var(--dash-line);
  font-family: var(--dash-font-mono);
  font-size: 0.8rem;
}

.debug-panel h4 {
  margin: 0 0 12px 0;
  color: var(--dash-warning);
}

.debug-panel pre {
  max-height: 200px;
  overflow: auto;
  background: var(--dash-ink);
  padding: 12px;
  border-radius: var(--dash-radius-xs);
}

.debug-btn {
  padding: 6px 12px;
  border: none;
  border-radius: var(--dash-radius-xs);
  background: var(--dash-primary);
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  margin-right: 8px;
}

.debug-btn.close {
  background: var(--dash-muted);
}

/* Animations */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .bill-item-main {
    grid-template-columns: 2fr 1fr 1.5fr 1fr;
    gap: 12px;
  }
  
  .bill-actions {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }
}

@media (max-width: 768px) {
  .bill-item-main {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .tenant-info {
    grid-row: 1;
  }
  
  .unit-info {
    grid-row: 2;
  }
  
  .bill-details {
    grid-row: 3;
  }
  
  .period-info {
    grid-row: 4;
  }
  
  .bill-actions {
    grid-row: 5;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0;
  }
  
  .action-buttons {
    margin-left: auto;
  }
}

@media (max-width: 480px) {
  .bills-page {
    padding: 10px;
  }
  
  .bills-header {
    padding: 12px 16px;
  }
  
  .header-title h2 {
    font-size: 1.1rem;
  }
  
  .btn-generate {
    padding: 6px 12px;
    min-width: 80px;
    font-size: 0.85rem;
  }
  
  .search-section {
    padding: 8px 16px;
  }
  
  .bills-list-container {
    padding: 16px;
  }
  
  .bill-item-main {
    padding: 12px;
  }
  
  .tenant-avatar {
    width: 36px;
    height: 36px;
    font-size: 0.85rem;
  }
}
</style>